import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, AlertTriangle, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Compute a viewport-clamped absolute position (top,left,width) anchored to
// the bottom-left of the trigger button. Falls back to edge-anchored fixed
// positioning on screens narrower than `compactBelowPx`.
function computeMenuPos(trigger, { preferredWidth = 320, margin = 8 }) {
  if (!trigger) return null;
  const r = trigger.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (vw < 640) {
    // Mobile: full-width below the topbar.
    return {
      top: Math.round(Math.max(r.bottom + 4, 56)),
      left: margin,
      width: vw - margin * 2,
      maxHeight: Math.round(vh - (r.bottom + 4) - 72), // leave room for bottom-tab bar
    };
  }
  // Desktop/tablet: anchor just below the trigger, clamped inside viewport.
  const width = Math.min(preferredWidth, vw - margin * 2);
  let left = r.left;
  if (left + width + margin > vw) left = vw - width - margin;
  if (left < margin) left = margin;
  return {
    top: Math.round(r.bottom + 4),
    left: Math.round(left),
    width,
    maxHeight: Math.round(vh - r.bottom - 24),
  };
}

export default function LanguageSwitcher({
  languages,
  current,
  onChange,
  loading,
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const [pos, setPos] = useState(null);

  const groups = useMemo(() => {
    const avail = [];
    const unavail = [];
    (languages || []).forEach((l) => {
      if (l.available) avail.push(l);
      else unavail.push(l);
    });
    return { avail, unavail };
  }, [languages]);

  const currentLang = (languages || []).find((l) => l.id === current);

  // Recompute position when the menu opens, the window resizes, or scrolls.
  useLayoutEffect(() => {
    if (!open) return;
    const recompute = () => setPos(computeMenuPos(triggerRef.current, { preferredWidth: 320 }));
    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  function toggle() {
    // eslint-disable-next-line no-console
    console.log("[noefforts] language button clicked, was open=", open);
    setOpen((o) => !o);
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        className={
          "flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-ide-border bg-ide-panel hover:bg-ide-surface hover:border-ide-borderHi transition-colors text-sm min-h-[36px] " +
          (compact
            ? "min-w-0 max-w-[120px]"
            : "gap-2 px-2.5 min-w-[180px]")
        }
        onClick={toggle}
        disabled={loading || (languages || []).length === 0}
      >
        <span className="font-medium text-ide-text truncate">
          {loading
            ? "Loading…"
            : currentLang
            ? currentLang.label
            : "Pick"}
        </span>
        {!compact && currentLang?.version && (
          <span className="text-[11px] text-ide-mute font-mono truncate max-w-[120px]">
            {currentLang.version.split(" ").slice(0, 2).join(" ")}
          </span>
        )}
        <ChevronDown className="ml-auto h-4 w-4 flex-none text-ide-mute" />
      </button>

      {createPortal(
        <AnimatePresence>
          {open && pos && (
            <>
              {/* Backdrop — only mounted while open, captures outside clicks. */}
              <motion.div
                key="ls-bg"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.12 }}
                onClick={() => setOpen(false)}
                style={{ zIndex: 98 }}
                className="fixed inset-0 bg-transparent"
              />
              <motion.div
                key="ls-fg"
                initial={{ opacity: 0, y: -4, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.98 }}
                transition={{ duration: 0.14, ease: "easeOut" }}
                style={{
                  position: "fixed",
                  top: pos.top,
                  left: pos.left,
                  width: pos.width,
                  maxHeight: pos.maxHeight,
                  zIndex: 100,
                  pointerEvents: "auto",
                }}
                className="rounded-lg border border-ide-border bg-ide-panel shadow-soft overflow-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-ide-mute border-b border-ide-border sticky top-0 bg-ide-panel z-10">
                  Available
                </div>
                {groups.avail.map((l) => (
                  <button
                    key={l.id}
                    className="flex w-full items-start gap-3 px-3 py-2 text-left text-sm hover:bg-ide-surface transition-colors min-h-[44px]"
                    onClick={() => {
                      onChange?.(l.id);
                      setOpen(false);
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-ide-text">{l.label}</span>
                        {current === l.id && (
                          <Check className="h-3.5 w-3.5 text-emerald-400" />
                        )}
                        {l.has_compile && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/30">
                            compile
                          </span>
                        )}
                        {l.web && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30">
                            web
                          </span>
                        )}
                      </div>
                      {l.version && (
                        <div className="text-[11px] text-ide-mute font-mono truncate">
                          {l.version}
                        </div>
                      )}
                    </div>
                  </button>
                ))}
                {groups.unavail.length > 0 && (
                  <>
                    <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-ide-mute border-b border-t border-ide-border bg-ide-panel2">
                      Not installed
                    </div>
                    {groups.unavail.map((l) => (
                      <div
                        key={l.id}
                        className="flex items-start gap-3 px-3 py-2 text-sm opacity-50 cursor-not-allowed"
                        title={"Runtime not installed in this environment"}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-ide-text">{l.label}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30 inline-flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              not installed
                            </span>
                          </div>
                          <div className="text-[11px] text-ide-mute font-mono">
                            {l.id}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
